/** Parse a CSV string into { headers, rows }. Handles quoted fields. */
export function parseCsv(text) {
  const raw = String(text || '')
    .replace(/^\uFEFF/, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();
  if (!raw) return { headers: [], rows: [] };

  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i];
    const next = raw[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') {
        field += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === ',') {
      row.push(field);
      field = '';
      continue;
    }
    if (ch === '\n') {
      row.push(field);
      field = '';
      if (row.some((c) => String(c).trim() !== '')) rows.push(row);
      row = [];
      continue;
    }
    field += ch;
  }
  row.push(field);
  if (row.some((c) => String(c).trim() !== '')) rows.push(row);

  const headers = (rows.shift() || []).map((h) => String(h).trim());
  return {
    headers,
    rows: rows.map((r) => {
      const out = headers.map((_, i) => (r[i] == null ? '' : String(r[i])));
      return out;
    }),
  };
}

function pdfEscape(text) {
  return String(text)
    .replace(/[^\x20-\x7E]/g, ' ')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

function looksLikeCsv(text) {
  const first = String(text || '')
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .find((l) => l.trim());
  if (!first || !first.includes(',')) return false;
  const cols = first.split(',').length;
  return cols >= 2 && cols <= 40;
}

/** Pull literal text from a simple PDF content stream (CSV-dump style PDFs). */
export function extractTextFromPdfBytes(bytes) {
  const latin1 = new TextDecoder('latin1').decode(bytes);
  const chunks = [];
  const re = /\(((?:\\.|[^\\)])*)\)\s*Tj/g;
  let m;
  while ((m = re.exec(latin1))) {
    const raw = m[1]
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\r')
      .replace(/\\t/g, '\t')
      .replace(/\\\(/g, '(')
      .replace(/\\\)/g, ')')
      .replace(/\\\\/g, '\\');
    chunks.push(raw);
  }
  if (chunks.length) return chunks.join('\n');

  // Fallback: some generators put bare CSV in the stream without Tj wrapping every line.
  const streamRe = /stream\r?\n([\s\S]*?)\r?\nendstream/g;
  const parts = [];
  while ((m = streamRe.exec(latin1))) {
    const body = m[1].replace(/^BT[\s\S]*?ET/g, (bt) => {
      // keep only printable runs
      return bt;
    });
    if (looksLikeCsv(body)) parts.push(body);
  }
  return parts.join('\n');
}

function colWidths(headers, pageWidth, margin) {
  const usable = pageWidth - margin * 2;
  const key = headers.map((h) => h.toLowerCase());
  const weights = key.map((h) => {
    if (h.includes('name') || h.includes('item')) return 2.4;
    if (h.includes('sku') || h.includes('id')) return 1.6;
    if (h.includes('margin') || h === 'qty') return 0.8;
    return 1;
  });
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  return weights.map((w) => (w / sum) * usable);
}

function fit(text, maxChars) {
  const s = String(text ?? '');
  if (s.length <= maxChars) return s;
  return `${s.slice(0, Math.max(0, maxChars - 3))}...`;
}

/**
 * Build a multi-page landscape table PDF from CSV headers/rows.
 * Returns a Blob (application/pdf).
 */
export function buildTablePdf({
  title = 'Report',
  subtitle = '',
  headers = [],
  rows = [],
}) {
  const pageW = 792; // landscape letter
  const pageH = 612;
  const margin = 28;
  const rowH = 14;
  const fontSize = 8;
  const headerFont = 8;
  const titleSize = 13;
  const widths = colWidths(headers, pageW, margin);

  const topY = pageH - margin - 36;
  const bottomY = margin + 16;
  const rowsPerPage = Math.max(1, Math.floor((topY - bottomY - 18) / rowH));
  const pages = [];
  for (let i = 0; i < rows.length || i === 0; i += rowsPerPage) {
    pages.push(rows.slice(i, i + rowsPerPage));
    if (rows.length === 0) break;
  }

  const contentObjects = [];
  pages.forEach((pageRows, pageIndex) => {
    const ops = [];
    let y = pageH - margin;

    ops.push('BT', `/F1 ${titleSize} Tf`, `1 0 0 1 ${margin} ${y - titleSize} Tm`, `(${pdfEscape(title)}) Tj`, 'ET');
    y -= titleSize + 8;
    if (subtitle) {
      ops.push(
        'BT',
        `/F1 9 Tf`,
        `1 0 0 1 ${margin} ${y - 9} Tm`,
        `(${pdfEscape(subtitle)}) Tj`,
        'ET'
      );
      y -= 16;
    }

    // header background
    const tableTop = y;
    ops.push('0.92 g', `${margin} ${tableTop - rowH} ${pageW - margin * 2} ${rowH} re`, 'f', '0 g');

    // header labels
    let x = margin + 3;
    ops.push('BT', `/F1 ${headerFont} Tf`);
    headers.forEach((h, i) => {
      const maxChars = Math.max(4, Math.floor(widths[i] / 4.2));
      ops.push(`1 0 0 1 ${x} ${tableTop - 10} Tm`, `(${pdfEscape(fit(h, maxChars))}) Tj`);
      x += widths[i];
    });
    ops.push('ET');
    y = tableTop - rowH;

    pageRows.forEach((row, ri) => {
      if (ri % 2 === 1) {
        ops.push('0.97 g', `${margin} ${y - rowH} ${pageW - margin * 2} ${rowH} re`, 'f', '0 g');
      }
      let cx = margin + 3;
      ops.push('BT', `/F1 ${fontSize} Tf`);
      row.forEach((cell, i) => {
        const maxChars = Math.max(4, Math.floor(widths[i] / 4.2));
        ops.push(`1 0 0 1 ${cx} ${y - 10} Tm`, `(${pdfEscape(fit(cell, maxChars))}) Tj`);
        cx += widths[i];
      });
      ops.push('ET');
      y -= rowH;
    });

    // footer
    ops.push(
      'BT',
      '/F1 8 Tf',
      `1 0 0 1 ${margin} ${margin - 4} Tm`,
      `(${pdfEscape(`Page ${pageIndex + 1} of ${pages.length}`)}) Tj`,
      'ET'
    );

    contentObjects.push(ops.join('\n'));
  });

  // Assemble PDF with xref
  const objects = [];
  objects.push('<< /Type /Catalog /Pages 2 0 R >>'); // 1
  const kids = contentObjects.map((_, i) => `${3 + i * 2} 0 R`).join(' ');
  objects.push(`<< /Type /Pages /Kids [${kids}] /Count ${contentObjects.length} >>`); // 2

  contentObjects.forEach((stream) => {
    const pageObjIndex = objects.length + 1;
    const contentIndex = pageObjIndex + 1;
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageW} ${pageH}] /Contents ${contentIndex} 0 R /Resources << /Font << /F1 ${3 + contentObjects.length * 2} 0 R >> >> >>`
    );
    objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
  });

  // Font object (shared) — index after all page/content pairs
  const fontIndex = objects.length + 1;
  // Fix page resources to point at the real font index
  for (let i = 0; i < contentObjects.length; i += 1) {
    const pageIdx = 2 + i * 2; // 0-based in objects array
    objects[pageIdx] = objects[pageIdx].replace(
      `/F1 ${3 + contentObjects.length * 2} 0 R`,
      `/F1 ${fontIndex} 0 R`
    );
  }
  objects.push('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');

  let body = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((obj, i) => {
    offsets.push(body.length);
    body += `${i + 1} 0 obj\n${obj}\nendobj\n`;
  });
  const xrefPos = body.length;
  let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (let i = 1; i <= objects.length; i += 1) {
    xref += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  }
  body += xref;
  body += `trailer<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefPos}\n%%EOF\n`;

  const bytes = new Uint8Array(body.length);
  for (let i = 0; i < body.length; i += 1) bytes[i] = body.charCodeAt(i) & 0xff;
  return new Blob([bytes], { type: 'application/pdf' });
}

/**
 * Turn a downloaded report file (CSV text, or PDF that only dumps CSV lines)
 * into a formatted table PDF when needed. Returns null if content should be
 * left as-is (already a non-CSV PDF, or empty).
 */
export async function formatReportDownloadBlob(sourceBlob, { title, subtitle, forcePdf } = {}) {
  const buf = await sourceBlob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const head = new TextDecoder('latin1').decode(bytes.slice(0, 8));
  let csvText = '';

  if (head.startsWith('%PDF')) {
    csvText = extractTextFromPdfBytes(bytes);
    if (!looksLikeCsv(csvText)) {
      // Mislabelled CSV (rare) or text drawn without standard Tj operators
      const asText = new TextDecoder('utf-8').decode(bytes);
      if (looksLikeCsv(asText)) csvText = asText;
      else if (!forcePdf) return null;
      else return null;
    }
  } else {
    csvText = new TextDecoder('utf-8').decode(bytes);
    if (!looksLikeCsv(csvText)) {
      if (!forcePdf) return null;
      // Still try to parse — forcePdf means the user asked for a table PDF
      if (!csvText.includes(',')) return null;
    }
  }

  const { headers, rows } = parseCsv(csvText);
  if (!headers.length) return null;

  return buildTablePdf({
    title: title || 'Report',
    subtitle: subtitle || '',
    headers,
    rows,
  });
}

export function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
